import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { compareAsc, addDays } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import toast from 'react-hot-toast';
import { 
  useTheme, 
  useMediaQuery, 
  CircularProgress, 
  Box, 
  Typography, 
  IconButton, 
  Tooltip,
  FormControl, 
  MenuItem, 
  InputLabel, 
  Select, 
} from '@mui/material';
import {
  Visibility,
  Sort,
} from '@mui/icons-material';
import { useDateRangeContext } from 'contexts/DateRangeContext';
import useAuthCheck from 'hooks/useAuthCheck';
import { LAYOUT_HEIGHT } from 'state/constants';
import api from 'state/axios';
import { fetchGroupedRosterDetailsByCrewCode } from "state/api";
import { filterCrewMember } from "state/helpers";
import { hasPermission } from 'state/checkPermission';
import DataGridComponent from 'components/DataGridComponent';
import Header from 'components/Header';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import StyledButton from 'components/StyledButton';
import FilterButton from 'components/FilterButton';
import CrewFilterDialog from 'components/CrewFilterDialog';
import RosterDetailsDialog from 'components/RosterDetailsDialog';


const hiddenColumns = { _id: false, post: false, base: false, trainingQuals: false, quals: false };
const nonMobileHiddenColumns = { _id: false, trainingQuals: false, quals: false };

const RosterDetails = () => {
  useAuthCheck();
  const user = useSelector((state) => state.user); 
  const timeMode = useSelector((state) => state.timemode);
  const crewRanksData = useSelector((state) => state.crewRanks);
  const theme = useTheme();
  const primaryDark = theme.palette.primary.dark;
  const textPrimaryColor = theme.palette.text.primary;
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));

  const canView = hasPermission(user?.permissions, "RosterDetails", "view", false);

  const { startDate, endDate } = useDateRangeContext();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [isFilterDialogOpen, setIsFilterDialogOpen] = useState(false);
  const [filters, setFilters] = useState({ isActive: 'ALL' });
  const defaultFilters = {
    isActive: 'ALL',
  };
  const [scheduleCodes, setScheduleCodes] = useState([]);
  const [sortOption, setSortOption] = useState('none');

  const [openRosterDetailsDialog, setOpenRosterDetailsDialog] = useState(false);
  const [selectedCrewCode, setSelectedCrewCode] = useState(null);
  const [selectedCrewName, setSelectedCrewName] = useState(null);

  const [rosterDetails, setRosterDetails] = useState([]);
  const [groupedCrewRosters, setGroupedCrewRosters] = useState([]);
  
  
  useEffect(() => {
    const fetchRosterDetails = async () => {
      if (!startDate || !endDate) return [];
      
      try {
        setError(null);
        setLoading(true);
  
        const url = '/rosterdetails';
        const payload = { 
          startDate: addDays(startDate, -1), 
          endDate: addDays(endDate, 1) 
        };
        
        const res = await api.post(url, payload);
        
        if (res.data) {        
          const filteredData = res?.data?.filter((roster) => {            
            const flightDateToCompare =
              timeMode === 'UTC' ? roster.flightDate : roster.flightDateLocal;
          
            return (
              compareAsc(flightDateToCompare, startDate) >= 0 &&
              compareAsc(flightDateToCompare, endDate) <= 0
            );
          });
          
          setRosterDetails(filteredData);  
        }
      } catch (error) {
        setError(error);
        toast.error(error?.message || "Failed to fetch roster details!");
      } finally {
        setLoading(false);
      }
    };
  
    fetchRosterDetails();
  }, [timeMode, startDate, endDate]); 
 
  const filteredRosterDetails = useMemo(() => {
    if (!Array.isArray(rosterDetails)) return [];

    let result = rosterDetails;

    if (filters) {
      result = result.filter(r => filterCrewMember(r, filters));
    }

    if (scheduleCodes?.length > 0) {
      result = result.filter(r => scheduleCodes.includes(r.flightNo));
    }

    return result;
  }, [rosterDetails, filters, scheduleCodes]);  
  

  const sortedRosterDetails = useMemo(() => {
    if (sortOption === 'none') return filteredRosterDetails;

    const sorted = [...filteredRosterDetails];
    sorted.sort((a, b) => {
        const aAtd = a.fullAtd ? new Date(a.fullAtd) : null;
        const bAtd = b.fullAtd ? new Date(b.fullAtd) : null;
        const aReg = a.flightReg || '';
        const bReg = b.flightReg || '';
               
        if (aReg !== bReg) return aReg.localeCompare(bReg);
        if (!aAtd && !bAtd) return 0;
        if (!aAtd) return 1;
        if (!aAtd) return -1;
        return aAtd - bAtd;
    });

    return sorted;
  }, [filteredRosterDetails, sortOption]);

  const handleSortToggle = () => {
    setSortOption((prev) => {
      if (prev === 'none') return 'fullAtd_crewName';
      if (prev === 'fullAtd_crewName') return 'none';
      return 'none';
    });
  };
  
  const handleFilterOpen = () => setIsFilterDialogOpen(true);
  const handleFilterClose = () => setIsFilterDialogOpen(false);
  
  const handleFilterApply = (filterValues) => {
    setFilters(filterValues); 
  };
  
  const handleViewClick = useCallback(async (param) => {
    try {
      const crewCode = param.crewCode;
      const crewName = param.crewName;
      setSelectedCrewCode(crewCode);
      setSelectedCrewName(crewName);

      const result = await fetchGroupedRosterDetailsByCrewCode(crewCode, startDate, endDate);
                 
      setGroupedCrewRosters(result);
      setOpenRosterDetailsDialog(true);
    } catch (error) {
      setOpenRosterDetailsDialog(false);
    } 
  }, [startDate, endDate]);

  const handleCloseRosterDetailsDialog = () => {
    setGroupedCrewRosters([]);
    setOpenRosterDetailsDialog(false);
  };

  const columns = useMemo(() => [
    {
      field: "no",
      headerName: "No",
      filterable: false,      
      sortable: false,
      renderCell: (params) => params.api.getAllRowIds().indexOf(params.id) + 1,
      ...(() => {
        return isMobile ? { width: 66 } : { flex: 1 };
      })(),
    },    
    {
      field: "crewCode",
      headerName: "Code",
      ...(() => {
        return isMobile ? { width: 90 } : { flex: 1 };
      })(),
    },    
    {
      field: "crewName",
      headerName: "CrewName",
      cellClassName: "name-column--cell",
      ...(() => {
        return isMobile ? { width: 300 } : { flex: 3.5 };
      })(),
    },
    {
      field: "post",
      headerName: "Post",
      ...(() => {
        return isMobile ? { width: 90 } : { flex: 1 };
      })(),
    },
    {
      field: "base",
      headerName: "Base",
      ...(() => {
        return isMobile ? { width: 90 } : { flex: 1 };
      })(),
    },
    {
      field: "trainingQuals",
      headerName: "Trainer",
      ...(() => {
        return isMobile ? { width: 90 } : { flex: 1 };
      })(),
    }, 
    {
      field: "quals",
      headerName: "Quals",
      ...(() => {
        return isMobile ? { width: 130 } : { flex: 1.5 };
      })(),
    }, 
    {
      field: "pax",
      headerName: "Pax",
      ...(() => {
        return isMobile ? { width: 90 } : { flex: 1 };
      })(),
    },
    // {
    //   field: "flightCarrier",
    //   headerName: "Carrier",
    //   ...(() => {
    //     return isMobile ? { width: 90 } : { flex: 1 };
    //   })(),
    // },    
    {
      field: "flightNo",
      headerName: "ScheduleCode",
      ...(() => {
        return isMobile ? { width: 110 } : { flex: 1.5 };
      })(),
    },
    {
      field: "dep",
      headerName: "Dep",
      ...(() => {
        return isMobile ? { width: 90 } : { flex: 1 };
      })(),
    },
    {
      field: "arr",
      headerName: "Arr",
      ...(() => {
        return isMobile ? { width: 90 } : { flex: 1 };
      })(),
    },
    {
      field: timeMode === 'UTC' ? 'fullAtd' : 'fullAtdLocal',
      headerName: "StartTime",
      headerClassName: 'theme-column--header',      
      valueFormatter: (value) => {
        if (!value) {
          return '';
        }
        return `${formatInTimeZone(value, 'UTC', "HH:mm dd/MM/yy")}`;
      },
      ...(() => {
        return isMobile ? { width: 125 } : { flex: 1.6 };
      })(),
    },
    {
      field: timeMode === 'UTC' ? "fullAta" : "fullAtaLocal",
      headerName: "EndTime",
      headerClassName: 'theme-column--header',
      valueFormatter: (value) => {
        if (value == null) {
          return '';
        }
        return `${formatInTimeZone(value, 'UTC', "HH:mm dd/MM/yy")}`;
      },
      ...(() => {
        return isMobile ? { width: 125 } : { flex: 1.6 };
      })(),
    },
    {
      field: "blockHours",
      headerName: "BH",
      cellClassName: 'right-align-cell',
      valueFormatter: (params) => {
        return params > 0 ? parseFloat(params).toFixed(2) : '';
      },
      ...(() => {
        return isMobile ? { width: 90 } : { flex: 1 };
      })(),
    },    
  ], [timeMode, isMobile]);

  const actionColumn = useMemo(() => {    
    const activeCount = [canView].filter(Boolean).length;
    if (activeCount === 0) return [];

    const baseWidth = 60 + activeCount * 25;

    return [
      {
        field: "action",
        headerName: "Action",
        filterable: false,
        sortable: false,
        width: baseWidth,
        renderCell: (params) => {
            return (
              <FlexCenter
                gap={1}
                sx={{ width: '100%', height: '100%' }}
              >
                <Tooltip title="View">
                  <IconButton
                    aria-label="View"
                    sx={{ color: "primary.dark" }}
                    onClick={() => { 
                      if (!canView) return;
                      handleViewClick(params.row);
                    }}
                  >
                    <Visibility />
                  </IconButton>
                </Tooltip>
              </FlexCenter>
            );
        },
      }
    ]
  }, [
    canView, 
    handleViewClick,
  ]); 
    
  const [columnVisibilityModel, setColumnVisibilityModel] = useState(
    !isDesktop ? hiddenColumns : nonMobileHiddenColumns
  );

  useEffect(() => {
    setColumnVisibilityModel(!isDesktop ? hiddenColumns : nonMobileHiddenColumns);
  }, [isDesktop]);

  const {
    scheduleCodeOptions,
    baseOptions,
    postOptions,
    trainingQualOptions,
    isActiveOptions,
  } = useMemo(() => {
    if (!Array.isArray(rosterDetails) || rosterDetails.length === 0) {
      return {
        scheduleCodeOptions: [],
        baseOptions: [],
        postOptions: [],
        trainingQualOptions: [],
        isActiveOptions: [],
      };
    }

    const baseSet = new Set();
    const postSet = new Set();
    const trainingQualSet = new Set();
    const isActiveSet = new Set();
    const scheduleMap = new Map();

    rosterDetails.forEach(r => {
      if (r.base) baseSet.add(r.base);
      if (r.post) postSet.add(r.post);
      if (r.isActive !== undefined) isActiveSet.add(r.isActive);

      r.trainingQuals?.forEach(q => q && trainingQualSet.add(q));

      if (!r.routeNo && r.flightNo && !scheduleMap.has(r.flightNo)) {
        scheduleMap.set(r.flightNo, r);
      }
    });
    
    const scheduleCodeOptions = Array.from(scheduleMap.values())
      .sort((a, b) => {
        const catA = a.category ?? "";
        const catB = b.category ?? "";

        if (catA !== catB) {
          return catA.localeCompare(catB);
        }

        return (a.flightNo ?? "").localeCompare(b.flightNo ?? "");
      })
      .map(r => ({
        scheduleCode: r.flightNo,
        description: r.description,
      }));

    
    const baseOptions = [...baseSet].sort();

    const postOptions = [...postSet]
      .map(post => crewRanksData.find(r => r.rankCode === post))
      .filter(Boolean)
      .sort((a, b) => a.rankPriority - b.rankPriority)
      .map(r => r.rankCode);

    const trainingQualOptions = [...trainingQualSet].sort();
    const isActiveOptions = [...isActiveSet].sort();

    return {
      scheduleCodeOptions,
      baseOptions,
      postOptions,
      trainingQualOptions,
      isActiveOptions,
    };
  }, [rosterDetails, crewRanksData]);
  
  const columnsToShow = [...columns, ...actionColumn];


  return (
    <Box className="main-container-box">
      <FlexBetween>
        <Header 
          title="Roster Details" 
          subtitle={`${loading ? 'Loading...' : 'List of Roster Details'}`}
        />
        {!loading && (
          <Box display="flex" alignItems="center" gap={1}>
            <FormControl
              sx={{ width: isMobile ? "6rem" : "12rem" }}
              size="small"
            >
              <InputLabel id="schedule-codes-label">Schedule Codes</InputLabel>
              <Select
                multiple
                label="Schedule Codes"
                value={scheduleCodes}
                onChange={(e) => setScheduleCodes(e.target.value)}
              >
                {scheduleCodeOptions?.map((item, index) => {
                  return (
                    <MenuItem key={index} value={item.scheduleCode}>
                      {item.scheduleCode} {item.description ? ` - ${item.description}` : ""}
                    </MenuItem>
                  )
                })}
              </Select>
            </FormControl>

            <Tooltip title={`Sort by ${sortOption === 'fullAtd_crewName' ? 'StartTime' : 'CrewCode'}`} arrow>
              <span>
                <StyledButton 
                  onClick={handleSortToggle}
                  sx={{
                    color: sortOption === 'none' 
                      ? `${textPrimaryColor} !important` 
                      : `${primaryDark} !important`,
                  }}
                >
                  <Sort />
                </StyledButton>
              </span>     
            </Tooltip>
            
            <FilterButton
              filters={filters}
              defaultFilters={defaultFilters}
              onClick={handleFilterOpen}
            />
          </Box>
        )}                    
      </FlexBetween>
      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" height={LAYOUT_HEIGHT}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Box display="flex" justifyContent="center" alignItems="center" height={LAYOUT_HEIGHT}>
          <Typography color="error">Error: {error?.message || ''}</Typography>
        </Box>
      ) : (
        <Box
          display="grid"
          gridTemplateColumns="repeat(12, minmax(0, 1fr))"
          gap={2}
        >
          <Box 
            height={LAYOUT_HEIGHT} 
            sx={{ gridColumn: "span 12", overflowY: 'auto' }}
          >
            <DataGridComponent
              rows={sortedRosterDetails}
              columns={columnsToShow}
              columnVisibilityModel={columnVisibilityModel}
              setColumnVisibilityModel={setColumnVisibilityModel}
              loading={loading}
              props={{ initialState: { pagination: { paginationModel: { pageSize: 100 } } } }}
            />
          </Box>
        </Box>
      )}

      {isFilterDialogOpen &&
        <CrewFilterDialog
          open={isFilterDialogOpen}
          onClose={handleFilterClose}
          onApply={handleFilterApply}
          postOptions={postOptions}
          baseOptions={baseOptions}
          trainingQualOptions={trainingQualOptions}
          isActiveOptions={isActiveOptions}
          currentFilters={filters}
        />
      }
      
      {openRosterDetailsDialog &&
        <RosterDetailsDialog 
          open={openRosterDetailsDialog}
          onClose={handleCloseRosterDetailsDialog}
          crewCode={selectedCrewCode}
          crewName={selectedCrewName}
          crewRosters={groupedCrewRosters}
        />
      }
    </Box>
  );
};

export default RosterDetails;
